'use client'

import cn from 'clsx'

import { Swiper, SwiperSlide } from 'swiper/react'
import { A11y, Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/pagination'

import { type Cta2Column as Cta2ColumnType } from '../../../../../apps/benmax/src/types/sanity.types'

import { Button, Copy, ImageBox, NavLink } from 'shared-components'
import { getSanityLink } from 'shared-functions'

import Video from '../../Video/Video'

import BlockArrow from '../../../images/blockArrow'

import s from './Cta2Column.module.sass'
import { R2Asset } from 'shared-functions/utils'

type ExtendedCta2ColumnType = Cta2ColumnType & {
    site: string
    largeVideo: any
    videoFile: R2Asset
}

function Cta2Column({
    title,
    copy,
    link,
    mainBackgroundColour,
    video,
    videoFile,
    type,
    testimonial,
    largeVideo,
    bigLink,
    bigLinkCarousel,
}: ExtendedCta2ColumnType) {
    const _link = link?.linkType === 'internal' || link?.linkType === 'external' ? getSanityLink(link) || null : null

    const largeVideoUrl = (() => {
        if (!largeVideo) {
            return null
        }

        if (largeVideo.videoFile) {
            return largeVideo.videoFile?.asset?.fileURL
        }

        if (largeVideo.video) {
            return largeVideo?.video?.asset?.url
        }

        return null
    })()

    const bigLinkContent = (link: any) => {
        const _link =
            link?.link?.linkType === 'internal' || link?.link?.linkType === 'external'
                ? getSanityLink(bigLink?.link) || null
                : null

        return (
            <div className={s.BigLink}>
                <NavLink href={_link?.url ? _link?.url : ''}>
                    {BlockArrow}
                    <div className={s.BigLinkImage}>
                        {link?.image && <ImageBox image={link?.image} alt={link?.title} />}
                    </div>
                    <div className={s.BigLinkContent}>
                        {link?.subtitle && (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: link.subtitle,
                                }}
                                className={cn(s.BigLinkTitle, 'smallCaps')}
                            />
                        )}
                        {link?.title && (
                            <h4
                                dangerouslySetInnerHTML={{
                                    __html: link.title,
                                }}
                            />
                        )}
                        {link?.copy && (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: link?.copy,
                                }}
                                className={cn(s.BigLinkCopy, 'smallCaps')}
                            />
                        )}
                    </div>
                </NavLink>
            </div>
        )
    }

    return (
        <section className={cn(s.Cta2Column, type === 'bigLinkCarousel' && s.BigLinkCarousel)}>
            <div className={cn(s.Cont, 'container')}>
                <div className={cn(s.Main, mainBackgroundColour === 'light' && s.Light)}>
                    <div>
                        {title && <h2 dangerouslySetInnerHTML={{ __html: title }} />}
                        {copy && (
                            <div className={s.Copy}>
                                <Copy copy={copy} />
                            </div>
                        )}
                        {_link && (
                            <Button
                                url={_link.url}
                                title={_link.title}
                                inverted={mainBackgroundColour !== 'light'}
                                transparent
                            />
                        )}
                    </div>
                    {video && (
                        <div className={s.VideoSection}>
                            <Video video={video} />
                        </div>
                    )}
                </div>
                {type === 'largeVideo' && (
                    <div className={cn(s.LargeVideo, largeVideo?.simple && s.Simple)}>
                        {largeVideoUrl && (
                            <div className={s.Video}>
                                <video
                                    className={s.Video}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    src={largeVideoUrl}
                                />
                            </div>
                        )}
                        {largeVideo?.title && (
                            <h3
                                dangerouslySetInnerHTML={{
                                    __html: largeVideo?.title,
                                }}
                            />
                        )}
                        {BlockArrow}
                    </div>
                )}
                {type === 'testimonial' && testimonial?.copy && (
                    <div className={s.Testimonial}>
                        <div>
                            {testimonial?.copy && <Copy copy={testimonial.copy} size="large bold" />}
                            <div className={s.Info}>
                                {testimonial?.company && (
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: testimonial.company,
                                        }}
                                    />
                                )}
                                {testimonial?.name && (
                                    <p
                                        dangerouslySetInnerHTML={{
                                            __html: testimonial.name,
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {type === 'bigLink' && bigLinkContent(bigLink)}
                {type === 'bigLinkCarousel' && (
                    <div className={s.Slider}>
                        <Swiper
                            modules={[A11y, Pagination]}
                            spaceBetween={10}
                            loop={true}
                            allowTouchMove={true}
                            pagination={{
                                clickable: true,
                            }}
                        >
                            {bigLinkCarousel?.map((link: any, index: number) => (
                                <SwiperSlide key={index}>{bigLinkContent(link)}</SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                )}
            </div>
        </section>
    )
}

export default Cta2Column
